/**
 * Error Handling Service
 * Provides comprehensive error handling, logging, and user-friendly error messages
 * Implements Requirements: 2.4, 3.1, 7.4
 */

import { notificationService, NotificationType } from './NotificationService';

export enum ErrorType {
  NETWORK = 'network',
  AUTHENTICATION = 'authentication',
  GAME_LOGIC = 'game_logic',
  STORAGE = 'storage',
  MULTIPLAYER = 'multiplayer',
  VALIDATION = 'validation',
  SYSTEM = 'system',
  TIMEOUT = 'timeout'
}

export enum ErrorSeverity {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  CRITICAL = 'critical'
}

export interface ErrorContext {
  userId?: string;
  gameId?: string;
  action?: string;
  component?: string;
  additionalData?: Record<string, any>;
}

export interface GameError {
  id: string;
  type: ErrorType;
  severity: ErrorSeverity;
  message: string;
  userMessage: string;
  timestamp: Date;
  context?: ErrorContext;
  stack?: string;
  recoveryActions?: RecoveryAction[];
}

export interface RecoveryAction {
  label: string;
  action: () => Promise<void> | void;
  primary?: boolean;
}

export interface ErrorHandlingOptions {
  showNotification?: boolean;
  logError?: boolean;
  reportError?: boolean;
  autoRetry?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
}

type ErrorListener = (error: GameError) => void;

export class ErrorHandlingService {
  private static instance: ErrorHandlingService;
  private errorLog: GameError[] = [];
  private listeners: ErrorListener[] = [];
  private errorCounter = 0;
  private maxLogSize = 100;

  private constructor() {
    this.setupGlobalErrorHandlers();
  }

  static getInstance(): ErrorHandlingService {
    if (!ErrorHandlingService.instance) {
      ErrorHandlingService.instance = new ErrorHandlingService();
    }
    return ErrorHandlingService.instance;
  }

  /**
   * Handle an error with comprehensive logging and user feedback
   */
  handleError(
    error: Error | string,
    type: ErrorType,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const gameError = this.createGameError(error, type, severity, context);
    
    const defaultOptions: ErrorHandlingOptions = {
      showNotification: true,
      logError: true,
      reportError: severity === ErrorSeverity.HIGH || severity === ErrorSeverity.CRITICAL,
      autoRetry: false,
      retryAttempts: 3,
      retryDelay: 1000
    };

    const finalOptions = { ...defaultOptions, ...options };

    // Log the error
    if (finalOptions.logError) {
      this.logError(gameError);
    }

    // Show user notification
    if (finalOptions.showNotification) {
      this.showErrorNotification(gameError);
    }

    // Report error for analytics/monitoring
    if (finalOptions.reportError) {
      this.reportError(gameError);
    }

    // Notify listeners
    this.notifyListeners(gameError);

    return gameError;
  }

  /**
   * Handle network connectivity errors specifically
   * Implements Requirements: 2.4, 3.1
   */
  handleNetworkError(
    error: Error | string,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const isOffline = !navigator.onLine;
    const userMessage = isOffline 
      ? 'You appear to be offline. Some features may be limited until connection is restored.'
      : 'Network connection issue. Please check your internet connection and try again.';

    const gameError = this.createGameError(
      error,
      ErrorType.NETWORK,
      isOffline ? ErrorSeverity.MEDIUM : ErrorSeverity.HIGH,
      context,
      userMessage
    );

    // Add network-specific recovery actions
    gameError.recoveryActions = [
      {
        label: 'Retry',
        action: async () => {
          if (context?.action && typeof window !== 'undefined') {
            // Emit retry event that components can listen to
            window.dispatchEvent(new CustomEvent('error-retry', { 
              detail: { context, originalError: error } 
            }));
          }
        },
        primary: true
      },
      {
        label: 'Continue Offline',
        action: () => {
          notificationService.sendInfoNotification(
            'Switched to offline mode. Single-player games are still available.'
          );
        }
      }
    ];

    // Handle the error with the pre-configured recovery actions
    const finalOptions = {
      ...options,
      showNotification: true
    };

    // Log the error
    if (finalOptions.logError !== false) {
      this.logError(gameError);
    }

    // Show user notification
    if (finalOptions.showNotification) {
      this.showErrorNotification(gameError);
    }

    // Report error for analytics/monitoring
    if (finalOptions.reportError !== false && gameError.severity === ErrorSeverity.HIGH) {
      this.reportError(gameError);
    }

    // Notify listeners
    this.notifyListeners(gameError);

    return gameError;
  }

  /**
   * Handle multiplayer connection errors
   * Implements Requirements: 2.4, 7.4
   */
  handleMultiplayerError(
    error: Error | string,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const gameError = this.createGameError(
      error,
      ErrorType.MULTIPLAYER,
      ErrorSeverity.HIGH,
      context,
      'Multiplayer connection issue. Attempting to reconnect...'
    );

    // Add multiplayer-specific recovery actions
    gameError.recoveryActions = [
      {
        label: 'Reconnect',
        action: async () => {
          window.dispatchEvent(new CustomEvent('multiplayer-reconnect', { 
            detail: { gameId: context?.gameId } 
          }));
        },
        primary: true
      },
      {
        label: 'Return to Menu',
        action: () => {
          window.dispatchEvent(new CustomEvent('navigate-to-menu'));
        }
      }
    ];

    return this.handleError(error, ErrorType.MULTIPLAYER, ErrorSeverity.HIGH, context, {
      autoRetry: true,
      retryAttempts: 3,
      retryDelay: 2000,
      ...options
    });
  }

  /**
   * Handle authentication errors
   */
  handleAuthenticationError(
    error: Error | string,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const gameError = this.createGameError(
      error,
      ErrorType.AUTHENTICATION,
      ErrorSeverity.MEDIUM,
      context,
      'Authentication failed. Please try logging in again.'
    );

    gameError.recoveryActions = [
      {
        label: 'Try Again',
        action: () => {
          window.dispatchEvent(new CustomEvent('auth-retry'));
        },
        primary: true
      },
      {
        label: 'Continue as Guest',
        action: () => {
          window.dispatchEvent(new CustomEvent('auth-guest-mode'));
        }
      }
    ];

    return this.handleError(error, ErrorType.AUTHENTICATION, ErrorSeverity.MEDIUM, context, options);
  }

  /**
   * Handle storage errors (localStorage, etc.)
   */
  handleStorageError(
    error: Error | string,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const gameError = this.createGameError(
      error,
      ErrorType.STORAGE,
      ErrorSeverity.MEDIUM,
      context,
      'Unable to save data locally. Your progress may not be preserved.'
    );

    gameError.recoveryActions = [
      {
        label: 'Continue',
        action: () => {
          notificationService.sendWarningNotification(
            'Continuing without local storage. Progress will not be saved.'
          );
        },
        primary: true
      }
    ];

    return this.handleError(error, ErrorType.STORAGE, ErrorSeverity.MEDIUM, context, options);
  }

  /**
   * Handle game logic validation errors
   */
  handleValidationError(
    error: Error | string,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const gameError = this.createGameError(
      error,
      ErrorType.VALIDATION,
      ErrorSeverity.LOW,
      context,
      'Invalid move. Please try a different action.'
    );

    return this.handleError(error, ErrorType.VALIDATION, ErrorSeverity.LOW, context, {
      showNotification: false, // Usually handled by UI feedback
      ...options
    });
  }

  /**
   * Handle timeout errors
   */
  handleTimeoutError(
    error: Error | string,
    context?: ErrorContext,
    options: ErrorHandlingOptions = {}
  ): GameError {
    const gameError = this.createGameError(
      error,
      ErrorType.TIMEOUT,
      ErrorSeverity.MEDIUM,
      context,
      'Request timed out. Please try again.'
    );

    gameError.recoveryActions = [
      {
        label: 'Retry',
        action: async () => {
          window.dispatchEvent(new CustomEvent('timeout-retry', { 
            detail: { context } 
          }));
        },
        primary: true
      }
    ];

    return this.handleError(error, ErrorType.TIMEOUT, ErrorSeverity.MEDIUM, context, {
      autoRetry: true,
      retryAttempts: 2,
      ...options
    });
  }

  /**
   * Get error history for debugging
   */
  getErrorHistory(): GameError[] {
    return [...this.errorLog];
  }

  /**
   * Clear error history
   */
  clearErrorHistory(): void {
    this.errorLog = [];
  }

  /**
   * Subscribe to error events
   */
  subscribe(listener: ErrorListener): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index !== -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Check if the application is in a recoverable state
   */
  isRecoverable(error: GameError): boolean {
    return error.severity !== ErrorSeverity.CRITICAL && 
           Boolean(error.recoveryActions) && 
           error.recoveryActions!.length > 0;
  }

  /**
   * Get user-friendly error message based on error type and context
   */
  private getUserFriendlyMessage(error: Error | string, type: ErrorType, context?: ErrorContext): string {
    const errorMessage = typeof error === 'string' ? error : error.message;

    switch (type) {
      case ErrorType.NETWORK:
        if (!navigator.onLine) {
          return 'You appear to be offline. Single-player games are still available.';
        }
        return 'Network connection issue. Please check your internet connection.';

      case ErrorType.MULTIPLAYER:
        if (context?.gameId) {
          return 'Lost connection to your multiplayer game. Attempting to reconnect...';
        }
        return 'Multiplayer service is temporarily unavailable. Please try again later.';

      case ErrorType.AUTHENTICATION:
        return 'Authentication failed. Please try logging in again.';

      case ErrorType.STORAGE:
        return 'Unable to save your progress locally. Your data may not be preserved.';

      case ErrorType.VALIDATION:
        return 'Invalid action. Please try again.';

      case ErrorType.TIMEOUT:
        return 'Request timed out. Please try again.';

      case ErrorType.GAME_LOGIC:
        return 'Game error occurred. Please restart the game if the issue persists.';

      case ErrorType.SYSTEM:
        return 'System error occurred. Please refresh the page if the issue persists.';

      default:
        return 'An unexpected error occurred. Please try again.';
    }
  }

  /**
   * Create a structured GameError object
   */
  private createGameError(
    error: Error | string,
    type: ErrorType,
    severity: ErrorSeverity,
    context?: ErrorContext,
    customUserMessage?: string
  ): GameError {
    const errorMessage = typeof error === 'string' ? error : error.message;
    const stack = typeof error === 'object' ? error.stack : undefined;
    
    return {
      id: `error-${++this.errorCounter}-${Date.now()}`,
      type,
      severity,
      message: errorMessage,
      userMessage: customUserMessage || this.getUserFriendlyMessage(error, type, context),
      timestamp: new Date(),
      context,
      stack
    };
  }

  /**
   * Log error to internal log and console
   */
  private logError(error: GameError): void {
    // Add to internal log
    this.errorLog.unshift(error);
    
    // Maintain log size limit
    if (this.errorLog.length > this.maxLogSize) {
      this.errorLog = this.errorLog.slice(0, this.maxLogSize);
    }

    // Console logging based on severity
    const logData = {
      id: error.id,
      type: error.type,
      severity: error.severity,
      message: error.message,
      context: error.context,
      timestamp: error.timestamp
    };

    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
        console.error('CRITICAL ERROR:', logData, error.stack);
        break;
      case ErrorSeverity.HIGH:
        console.error('HIGH SEVERITY ERROR:', logData);
        break;
      case ErrorSeverity.MEDIUM:
        console.warn('MEDIUM SEVERITY ERROR:', logData);
        break;
      case ErrorSeverity.LOW:
        console.info('LOW SEVERITY ERROR:', logData);
        break;
    }
  }

  /**
   * Show user notification for error
   */
  private showErrorNotification(error: GameError): void {
    const notificationType = error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.HIGH
      ? NotificationType.ERROR
      : NotificationType.WARNING;

    const persistent = error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.HIGH;

    notificationService.addNotification({
      type: notificationType,
      title: error.severity === ErrorSeverity.CRITICAL ? 'Critical Error' : 'Error',
      message: error.userMessage,
      persistent,
      actionLabel: error.recoveryActions?.[0]?.label,
      onAction: error.recoveryActions?.[0]?.action
    });
  }

  /**
   * Report error for monitoring/analytics
   */
  private reportError(error: GameError): void {
    // In a production environment, this would send to error reporting service
    // For now, we'll just log it as a reportable error
    console.log('REPORTING ERROR:', {
      id: error.id,
      type: error.type,
      severity: error.severity,
      message: error.message,
      context: error.context,
      timestamp: error.timestamp,
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Could integrate with services like Sentry, LogRocket, etc.
    // if (window.Sentry) {
    //   window.Sentry.captureException(new Error(error.message), {
    //     tags: {
    //       errorType: error.type,
    //       severity: error.severity
    //     },
    //     extra: error.context
    //   });
    // }
  }

  /**
   * Notify all error listeners
   */
  private notifyListeners(error: GameError): void {
    this.listeners.forEach(listener => {
      try {
        listener(error);
      } catch (listenerError) {
        console.error('Error in error listener:', listenerError);
      }
    });
  }

  /**
   * Setup global error handlers
   */
  private setupGlobalErrorHandlers(): void {
    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError(
        event.reason || 'Unhandled promise rejection',
        ErrorType.SYSTEM,
        ErrorSeverity.HIGH,
        { component: 'global' },
        { reportError: true }
      );
    });

    // Handle global JavaScript errors
    window.addEventListener('error', (event) => {
      this.handleError(
        event.error || event.message,
        ErrorType.SYSTEM,
        ErrorSeverity.HIGH,
        { 
          component: 'global',
          additionalData: {
            filename: event.filename,
            lineno: event.lineno,
            colno: event.colno
          }
        },
        { reportError: true }
      );
    });

    // Handle network status changes
    window.addEventListener('online', () => {
      notificationService.sendSuccessNotification('Connection restored');
    });

    window.addEventListener('offline', () => {
      this.handleNetworkError(
        'Network connection lost',
        { action: 'network_status_change' },
        { showNotification: true }
      );
    });
  }
}

// Export singleton instance
export const errorHandlingService = ErrorHandlingService.getInstance();